import React, { useState, useContext, useEffect, useRef } from "react";
import { PhotoManagerContext } from "..";
import { Folder, FolderBreadcrumbType } from "../types";
import {
  fetchFoldersList,
  fetchSubFolders,
  fetchFolderDetails,
  handleAddShortcut
} from "../../API/api";
import Bredcrumb from "./Breadcrumb";
import { usePopupClickOutside } from "../../../../hooks/usePopupClickOutside";
const folderIcon = require("../../styles/images/folder-icon.svg");
const folderBackIcon = require("../../styles/images/folder-back.svg");

interface FolderFeatureProps {
  onClose: () => void;
  onSuccess: (folder: any) => void;
  sourceEntityId: number;
  sourceEntityName?: string;
  sourceEntityType: "LibraryFolder" | "Event";
  sourceParentFolderId?: number | null;
}

const FolderFeature: React.FC<FolderFeatureProps> = ({
  onClose,
  onSuccess,
  sourceEntityId,
  sourceEntityName,
  sourceEntityType,
  sourceParentFolderId = null
}) => {
  const popupRef = useRef<HTMLDivElement>(null);

  const [loading, setLoading] = useState<boolean>(false);
  const context = useContext(PhotoManagerContext);
  const [foldersList, setFoldersList] = useState<Folder[]>([]);
  const [filteredFoldersList, setFilteredFoldersList] = useState<Folder[]>([]);
  const [listLoader, setListLoader] = useState<boolean>(true);
  const [currentFolderId, setCurrentFolderId] = useState<number | null>(null);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [activeFolder, setActiveFolder] = useState<FolderBreadcrumbType | null>(
    null
  );
  const [isLoadingFolder, setIsLoadingFolder] = useState<boolean>(false);

  usePopupClickOutside(popupRef as React.RefObject<HTMLElement>, () => {
    onClose();
  });

  const loadRootFolders = async () => {
    try {
      const response = await fetchFoldersList();
      if (response && response.entities) {
        setFoldersList(response.entities);
        setFilteredFoldersList(response.entities);
        setListLoader(false);
      }
    } catch (error) {
      console.error("Error fetching root folders:", error);
      setListLoader(false);
    }
  };

  useEffect(() => {
    loadRootFolders();
  }, []);

  useEffect(() => {
    let cancelled = false;

    const loadFolderDetails = async () => {
      if (!currentFolderId) {
        setActiveFolder(null);
        return;
      }

      try {
        setIsLoadingFolder(true);
        const data = await fetchFolderDetails(currentFolderId) as FolderBreadcrumbType | null;
        if (!cancelled) setActiveFolder(data);
      } catch (error) {
        console.error("Error fetching folder details:", error);
      } finally {
        if (!cancelled) setIsLoadingFolder(false);
      }
    };

    loadFolderDetails();
    return () => {
      cancelled = true;
    };
  }, [currentFolderId]);

  const onFoldersFetchSuccess = (newFolders: Folder[], folderId: number) => {
    setFoldersList(newFolders);
    setFilteredFoldersList(newFolders);
    setCurrentFolderId(folderId);
    setSearchQuery("");
    setListLoader(false);
  };

  const handleFolderOpen = async (folderId: number) => {
    try {
      setListLoader(true);
      const response = await fetchSubFolders(folderId, 1);
      if (response && response.entities) {
        onFoldersFetchSuccess(response.entities, folderId);
      }
    } catch (error) {
      console.error("Error fetching subfolders:", error);
      setListLoader(false);
    }
  };

  const handleShortcutFunction = async (
    targetParentFolderId: number | null
  ) => {
    const success = await handleAddShortcut(
      sourceParentFolderId,
      targetParentFolderId,
      sourceEntityType,
      sourceEntityId,
      setLoading
    );

    if (success) {
      const shortcutData = {
        id: success.entity_id,
        name: sourceEntityName,
        referenceId: success.id,
        type: success.entity_type === "LibraryFolder" ? "folder" : "event",
        sourceEntityId: success.entity_id,
        sourceEntityType: success.entity_type,
        targetParentFolderId: success.parent_folder_id
      };

      onSuccess?.(shortcutData);
      onClose();
    }
  };

  const goToLibrary = () => {
    setCurrentFolderId(null);
    setSearchQuery("");
    setListLoader(true);
    loadRootFolders();
  };

  const canCreateShortcutHere = (targetFolderId: number | null) => {
    // Allow nested events to create shortcuts in the root
    if (
      sourceEntityType === "Event" &&
      sourceParentFolderId !== null &&
      targetFolderId === null
    ) {
      return true;
    }
    // Can't create shortcut in same parent folder
    if (sourceParentFolderId === targetFolderId) {
      return false;
    }
    // Root folders can't create shortcuts in root
    if (sourceParentFolderId === null && targetFolderId === null) {
      return false;
    }
    if (sourceEntityId === targetFolderId) return false;
    return true;
  };

  const handleSearch = (query: string) => {
    setSearchQuery(query);
    if (query.trim() === "") {
      setFilteredFoldersList(foldersList);
    } else {
      const filtered = foldersList.filter(folder =>
        folder.name.toLowerCase().includes(query.toLowerCase())
      );
      setFilteredFoldersList(filtered);
    }
  };

  return (
    <>
      <div id="filter-dialog" className="modal fade in geo-modal" role="dialog">
        <div
          className="sc-add-modal modal-dialog filter-dialog-content"
          ref={popupRef}
        >
          <div className="sc-modal-head-info">
            <p>Select a location to create a shortcut</p>
            <h4 className="sc-modal-head-name">{sourceEntityName}</h4>
          </div>
          <button
            type="button"
            className="close"
            onClick={onClose}
            aria-label="Close"
          >
            <span aria-hidden="true">&times;</span>
          </button>
          <div className="sc-modal-content modal-content">
            <div className="sc-breadcrumb-container">
              {currentFolderId ? (
                <Bredcrumb
                  folderId={currentFolderId}
                  goToLibrary={goToLibrary}
                  handleFolderOpen={handleFolderOpen}
                  activeFolder={activeFolder}
                  isLoadingFolder={isLoadingFolder}
                />
              ) : (
                <div className="ws-breadcrumbs ws-action-and-breadcrumbs folder-breadcrumbs">
                  <div className="album-breadcrumb-left-content">
                    <p className="ws-head-txt">
                      <span
                        className="grey-text grey-text-limit-w"
                        data-toggle="tooltip"
                        data-placement="bottom"
                      >
                        Library
                      </span>
                      <span className="grey-text bc-links-divider"> / </span>
                    </p>
                  </div>
                </div>
              )}
            </div>

            <div className="sc-folder-modal-body">
              <div className="sc-folder-list">
                {currentFolderId && (
                  <div className="sc-folder-item">
                    <div
                      className="ws-event-group folder-back-container"
                      onClick={() => {
                        if (activeFolder?.parentFolderId) {
                          handleFolderOpen(activeFolder.parentFolderId);
                        } else {
                          goToLibrary();
                        }
                      }}
                    >
                      <img
                        src={folderBackIcon}
                        alt="folder-back"
                        className="ws-file-spacing"
                      />
                      <span className="sc-folder-name">Back...</span>
                    </div>
                  </div>
                )}
                {listLoader ? (
                  <div className="sc-no-folders sc-nof-spin">
                    <span className="fa fa-spinner fa-spin"></span>
                    <br />
                    Loading folders...
                  </div>
                ) : filteredFoldersList.length > 0 ? (
                  filteredFoldersList
                    .filter(folder => !folder.isReference)
                    .map((folder, index) => (
                      <div
                        key={folder.id}
                        className={`sc-folder-item ${
                          canCreateShortcutHere(folder.id)
                            ? "sc-can-create"
                            : "sc-no-create"
                        }`}
                        onClick={() => {
                          canCreateShortcutHere(folder.id)
                            ? handleFolderOpen(folder.id)
                            : null;
                        }}
                      >
                        <div>
                          <span className="ws-event-group">
                            <img
                              src={folderIcon}
                              className="ws-file-spacing"
                              alt="folder"
                            />
                            <span className="sc-folder-name">
                              {folder.name}
                              <span className="hide-if-same-folder">
                                Cannot create shortcut in the same parent folder
                              </span>
                            </span>
                          </span>
                        </div>
                        <div className="sc-folder-actions-hoverable">
                          <span
                            className="sc-folder-action-link"
                            onClick={() => {
                              canCreateShortcutHere(folder.id)
                                ? handleFolderOpen(folder.id)
                                : null;
                            }}
                          >
                            open
                          </span>
                          <span className="sc-fl-divider">|</span>
                          <span
                            className="sc-folder-action-link"
                            onClick={() =>
                              canCreateShortcutHere(folder.id)
                                ? handleShortcutFunction(folder.id)
                                : null
                            }
                          >
                            add
                          </span>
                        </div>
                      </div>
                    ))
                ) : searchQuery ? (
                  <div>No folders found matching "{searchQuery}"</div>
                ) : (
                  <div className="sc-no-folders">No folders here</div>
                )}
              </div>
              <div
                className={`sc-add-shortcut-btn ${
                  canCreateShortcutHere(currentFolderId)
                    ? "sc-enabled-btn"
                    : "sc-disabled-btn"
                }`}
                onClick={() =>
                  canCreateShortcutHere(currentFolderId)
                    ? handleShortcutFunction(currentFolderId)
                    : null
                }
                style={{
                  cursor:
                    loading || canCreateShortcutHere(currentFolderId)
                      ? "pointer"
                      : "not-allowed"
                }}
              >
                {loading ? (
                  <div className="loading-spinner">
                    <span className="fa fa-spinner fa-spin"></span>
                  </div>
                ) : (
                  `Add shortcut`
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="modal-backdrop fade in" />
    </>
  );
};

export default FolderFeature;
